import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type { ServiceParticipantsMapper } from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper'
import type {
  APIBookingDraft,
  APIBookingDraftServiceCancellationInsurance,
  APIBookingDraftServiceCancellationInsuranceOption,
} from '@/scopes/checkout/infrastructure/api/types'
import { isNil } from 'ramda'
import { createServiceParticipantsMapper } from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper'

import { type CheckoutPerBookingService, type ServiceMapper, ServiceType } from '../../../domain/types'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceBannersMapper?: ServiceBannersMapper
  serviceOptionMapper?: ServiceOptionMapper
  serviceType: ServiceType
}

export const createCancellationInsuranceServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutPerBookingService> = (dependencies) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
    serviceOptionMapper = createServiceOptionMapper(),
    serviceType,
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(
      ({ aName }) => aName === serviceName,
    ) as APIBookingDraftServiceCancellationInsurance

    if (!service)
      return null

    const { aName, options } = service
    const selectedValue = mapSelectedValue(service)
    return {
      id: aName,
      selectedValue,
      isSelected: !isNil(selectedValue),
      type: ServiceType.CancellationInsurance,
      features: mapFeatures(service),
      options: options
        .sort((a, b) => Number.parseFloat(a.percentage ?? '') - Number.parseFloat(b.percentage ?? ''))
        .map(option => mapOption(option)),
      banners: serviceBannersMapper.map(service),
      participants: serviceParticipantsMapper.map(service, bookingDraft),
    }
  }

  function mapOption(option: APIBookingDraftServiceCancellationInsuranceOption) {
    const { bestValueText, code, percentage, modalInformation, features, text, percentageText } = option

    return {
      label: text ?? '',
      value: code,
      percentage: !percentage?.includes('0,0') ? percentage : '',
      percentageText,
      price: 0,
      features,
      modalInformation,
      ...(bestValueText && { badge: bestValueText }),
    }
  }

  const mapFeatureIcon = (icon: string): IconGlyph | undefined => {
    const featureIconMap: Record<string, IconGlyph> = {
      'money': 'PaymentCardWithCoins',
      'health-and-safety': 'ShieldWithPlus',
      'luggage': 'Suitcase',
      '1-person-travelling': 'PersonWithSuitcase',
    }

    return featureIconMap[icon]
  }

  function mapFeatures(service: APIBookingDraftServiceCancellationInsurance) {
    if (!service.features)
      return null
    const { features } = service
    return features.map((feature) => {
      return {
        description: feature.description,
        code: feature.code,
        icon: mapFeatureIcon(feature.icon) ?? null,
        extraContent: feature.extraContent ?? {},
      }
    })
  }

  function mapSelectedValue({ options }: APIBookingDraftServiceCancellationInsurance) {
    return options.find(({ isSelected }) => isSelected)?.code || null
  }

  function mapSelection(service: CheckoutPerBookingService) {
    const { participants } = service

    return Object.values(participants ?? {}).map(({ id, selectedValue }) => ({
      participantId: id,
      value: selectedValue,
    }))
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType).map((item, index) => {
      const option = service.options[index]
      const mapped = mapOption(option)
      return {
        ...item,
        price: mapped.percentage || '0%',
      }
    })
  }

  function mapSelectedItemToGA4(service: CheckoutPerBookingService, step: number): ImpressionViewAncillariesItem[] {
    if (!service.selectedValue)
      return []

    const option = service.options.find(o => o.value === service.selectedValue)
    if (!option)
      return []

    const input: SelectedItemInput = {
      label: option.label,
      value: option.value || '',
      badge: option.badge,
    }

    return mapDefaultSelectedItemToGA4([input], {
      serviceType,
      serviceId: service.id,
      step,
    }).map(item => ({
      ...item,
      price: option.percentage || '0%',
    }))
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
